/**
 * Order lifecycle reconciliation (#679): turns order status events (WS orderUpdates via `orderEvents`), live
 * fills (WS userFills) and the ticket's submit responses into one toast per terminal state:
 *
 *   filled            orderUpdates status "filled", an immediate fill in the submit response, or live fills that
 *                     add up to the order's original size                       key <user>:<oid>:filled
 *   partially filled  a live fill that does not complete the order (after a short settle delay, so a full fill
 *                     whose "filled" status lands right behind it toasts only once)  key <user>:fill:<tid>
 *   canceled          any *Canceled status (reason unless canceled by the user)   key <user>:<oid>:canceled
 *   rejected          any *Rejected status, or a per-order error in the submit response (with reason). HL reports
 *                     some rejections twice (#747): an IOC that cannot fill gets an error status in the /exchange
 *                     response AND a WS `iocCancelRejected` event. The ticket announces each submit with
 *                     noteSubmitting(); a WS rejection that matches an in-flight (or just answered) submit by coin,
 *                     side, price and size is left to the submit response, which carries HL's reason text. This
 *                     works whichever of the two arrives first.
 *
 * The submit response's oid links the order to later updates: its human label ("Buy 10 Yes @ 63¢") and original
 * size are remembered per oid. Nothing toasts twice: keys are deduped by the toast store, fills by tid, userFills
 * snapshot frames (sent again on every (re)subscribe) never toast, and fills older than `maxFillAgeMs` are ignored.
 *
 * Fills while the tab slept (#1809 W12) have no live frame, only the snapshot on return: expectAway() makes that one
 * snapshot give ONE toast for everything since the tab was hidden, which also covers a live toast that came and went
 * unseen. It names the fill when one order is involved and counts them otherwise. Never one toast per old fill, and the
 * rows themselves are in the session's fills. Orders canceled or rejected while away have no replay: no toast.
 */
import { toasts, type ToastInput } from '../../components/toastStore';
import type { BasicOrder, UserFill } from '../../hl/types';
import { describeStatus } from './model';
import { t } from '../../i18n/i18n';

export interface LifecycleFormat {
  /** Market label for a coin, e.g. "#24393 Yes". */
  label: (coin: string) => string;
  /** Price for display, e.g. "63.2¢". */
  px: (px: string, coin: string) => string;
}

export interface LifecycleOptions {
  sink: (t: ToastInput) => boolean;
  /** Delay before a fill that did not complete its order toasts as "partially filled" (default 750 ms). */
  settleMs?: number;
  /** Ignore live fills older than this (default 2 min): catches backfills replayed after a reconnect. */
  maxFillAgeMs?: number;
  /** How long after the submit response a matching WS rejection is still treated as its echo (default 10 s). */
  submitGraceMs?: number;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (h: unknown) => void;
}

/** An order the ticket is submitting (or just submitted). */
interface PendingSubmit {
  user: string;
  coin: string;
  side: 'B' | 'A';
  px: number;
  sz: number;
  /** Matching window end: Infinity while the request is in flight, then now + submitGraceMs. */
  until: number;
}

interface Known {
  label?: string;
  origSz?: number;
  filled: number;
}

const DEFAULT_FORMAT: LifecycleFormat = {
  label: (coin) => coin,
  px: (px) => px,
};

/** Rows in a userFills snapshot. */
const SNAPSHOT_ROWS = 30;
const AWAY_TOAST_MS = 10_000;

function num(s: string | undefined): number | undefined {
  if (s === undefined) return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

function fmtSz(n: number): string {
  return String(Number(n.toFixed(8)));
}

export class OrderLifecycle {
  fmt: LifecycleFormat = DEFAULT_FORMAT;
  private known = new Map<string, Known>();
  private seenTids = new Set<string>();
  private timers = new Map<string, unknown[]>();
  /** <user>:<oid> that reached a terminal state here (pending "partially filled" toasts are dropped). */
  private terminal = new Set<string>();
  private readonly settleMs: number;
  private readonly maxFillAgeMs: number;
  private readonly now: () => number;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (h: unknown) => void;
  private submitSeq = 0;
  /** User -> time the tab was hidden, until the snapshot of its return. */
  private away = new Map<string, number>();
  private pending: PendingSubmit[] = [];
  private readonly submitGraceMs: number;

  constructor(private readonly opts: LifecycleOptions) {
    this.settleMs = opts.settleMs ?? 750;
    this.maxFillAgeMs = opts.maxFillAgeMs ?? 120_000;
    this.submitGraceMs = opts.submitGraceMs ?? 10_000;
    this.now = opts.now ?? Date.now;
    this.setTimer = opts.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = opts.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  }

  // ---- submit-side links -----------------------------------------------------------------------

  /**
   * The ticket is about to send this order. Returns `settle`, to call once the /exchange call is over (any outcome):
   * from then on a matching WS rejection is still swallowed for `submitGraceMs`.
   */
  noteSubmitting(user: string, o: { coin: string; isBuy: boolean; px: string; sz: string }): () => void {
    this.prunePending();
    const p: PendingSubmit = {
      user: norm(user),
      coin: o.coin,
      side: o.isBuy ? 'B' : 'A',
      px: Number(o.px),
      sz: Number(o.sz),
      until: Number.POSITIVE_INFINITY,
    };
    this.pending.push(p);
    if (this.pending.length > 50) this.pending.shift();
    return () => {
      if (p.until === Number.POSITIVE_INFINITY) p.until = this.now() + this.submitGraceMs;
    };
  }

  /** Link an accepted (resting) order's oid to its human label and original size. */
  noteSubmitted(user: string, oid: number, info: { label?: string; origSz?: string }): void {
    const k = this.entry(user, oid);
    if (info.label) k.label = info.label;
    const o = num(info.origSz);
    if (o !== undefined) k.origSz = o;
  }

  /** The submit response said "filled" right away. */
  noteImmediateFill(user: string, oid: number | undefined, info: { label: string; totalSz: string; avgPx: string; coin?: string }): void {
    const px = info.coin ? this.fmt.px(info.avgPx, info.coin) : info.avgPx;
    const body = t('orders.toast.filled_avg', { label: info.label, sz: fmtSz(num(info.totalSz) ?? 0), px });
    if (oid === undefined) {
      this.opts.sink({ key: `${norm(user)}:submit:${++this.submitSeq}:filled`, tone: 'success', title: t('orders.toast.filled'), body });
      return;
    }
    this.noteSubmitted(user, oid, { label: info.label });
    this.cancelTimers(user, oid);
    this.opts.sink({ key: `${norm(user)}:${oid}:filled`, tone: 'success', title: t('orders.toast.filled'), body });
  }

  /** A per-order error in the submit response (never reached the book). */
  noteSubmitError(user: string, label: string, message: string): void {
    this.opts.sink({
      key: `${norm(user)}:submit:${++this.submitSeq}:rejected`,
      tone: 'error',
      title: t('orders.toast.rejected'),
      body: `${label} · ${message}`,
    });
  }

  // ---- stream side ----------------------------------------------------------------------------

  /** One orderUpdates event (already de-duplicated by the orderEvents emitter). */
  onOrderUpdate(user: string, status: string, order: BasicOrder): void {
    const u = norm(user);
    const k = this.entry(u, order.oid);
    if (k.origSz === undefined) k.origSz = num(order.origSz);
    const st = describeStatus(status);
    const what = this.describe(k, order);
    switch (st.tone) {
      case 'filled':
        this.cancelTimers(u, order.oid);
        this.opts.sink({ key: `${u}:${order.oid}:filled`, tone: 'success', title: t('orders.toast.filled'), body: what });
        break;
      case 'canceled': {
        this.cancelTimers(u, order.oid);
        const partial = k.filled > 0 && k.origSz !== undefined ? ` ${t('orders.toast.part_of', { filled: fmtSz(k.filled), orig: fmtSz(k.origSz) })}` : '';
        const reason = status !== 'canceled' && st.reason ? ` · ${st.reason}` : '';
        this.opts.sink({ key: `${u}:${order.oid}:canceled`, tone: 'info', title: t('orders.toast.canceled'), body: `${what}${partial}${reason}` });
        break;
      }
      case 'rejected':
        this.cancelTimers(u, order.oid);
        // The echo of a rejection the submit response reports (with HL's text): toast once, from there (#747).
        if (this.takePending(u, order)) break;
        this.opts.sink({
          key: `${u}:${order.oid}:rejected`,
          tone: 'error',
          title: t('orders.toast.rejected'),
          body: `${what}${st.reason ? ` · ${st.reason}` : ''}`,
        });
        break;
      default:
        // open / triggered: not terminal.
        break;
    }
  }

  /** The tab slept: the next snapshot for `user` sums up the fills from `since` on. */
  expectAway(user: string, since: number): void {
    const u = norm(user);
    this.away.set(u, Math.min(this.away.get(u) ?? since, since));
  }

  private toastAway(u: string, snapshot: readonly UserFill[], since: number): void {
    const fills = snapshot.filter((f) => f.time >= since);
    const first = fills[0];
    if (!first) return;
    // Every row of a full snapshot is new, so there may be more.
    const more = fills.length === snapshot.length && fills.length >= SNAPSHOT_ROWS;
    const oneOrder = !more && fills.every((f) => f.oid === first.oid);
    const newest = fills.reduce((a, f) => (f.time > a.time ? f : a));
    let body: string;
    if (oneOrder) {
      const k = this.entry(u, first.oid);
      const label = k.label ?? t('orders.toast.side_label', { action: t(first.side === 'B' ? 'ticket.buy' : 'ticket.sell'), label: this.fmt.label(first.coin) });
      const sz = fills.reduce((sum, f) => sum + (num(f.sz) ?? 0), 0);
      body = t('orders.toast.fill', { label, sz: fmtSz(sz), px: this.fmt.px(newest.px, newest.coin) });
    } else body = t('orders.toast.away_many', { fills: more ? `${fills.length}+` : String(fills.length) });
    this.opts.sink({ key: `${u}:away:${newest.tid}`, tone: 'success', title: t('orders.toast.away'), body, ttlMs: AWAY_TOAST_MS });
  }

  /** A userFills frame. Snapshot frames (first frame of every (re)subscribe) only mark tids as seen. */
  onFills(user: string, fills: readonly UserFill[], isSnapshot: boolean): void {
    const u = norm(user);
    const since = this.away.get(u);
    if (isSnapshot && since !== undefined) {
      this.away.delete(u);
      this.toastAway(u, fills, since);
    }
    const cutoff = this.now() - this.maxFillAgeMs;
    for (const f of fills) {
      const tk = `${u}:${f.tid}`;
      if (this.seenTids.has(tk)) continue;
      this.seenTids.add(tk);
      if (this.seenTids.size > 5_000) this.seenTids.delete(this.seenTids.values().next().value as string);
      if (isSnapshot || f.time < cutoff) continue;
      const k = this.entry(u, f.oid);
      k.filled += num(f.sz) ?? 0;
      const label = k.label ?? t('orders.toast.side_label', { action: t(f.side === 'B' ? 'ticket.buy' : 'ticket.sell'), label: this.fmt.label(f.coin) });
      if (k.origSz !== undefined && k.filled + 1e-9 >= k.origSz) {
        this.cancelTimers(u, f.oid);
        this.opts.sink({
          key: `${u}:${f.oid}:filled`,
          tone: 'success',
          title: t('orders.toast.filled'),
          body: t('orders.toast.fill', { label, sz: fmtSz(k.origSz), px: this.fmt.px(f.px, f.coin) }),
        });
        continue;
      }
      const of = k.origSz !== undefined ? ` ${t('orders.toast.of', { filled: fmtSz(k.filled), orig: fmtSz(k.origSz) })}` : '';
      const body = `${t('orders.toast.fill', { label, sz: fmtSz(num(f.sz) ?? 0), px: this.fmt.px(f.px, f.coin) })}${of}`;
      const key = `${u}:fill:${f.tid}`;
      const h = this.setTimer(() => {
        this.dropTimer(u, f.oid, h);
        if (this.terminal.has(`${u}:${f.oid}`)) return;
        this.opts.sink({ key, tone: 'info', title: t('orders.toast.partial'), body });
      }, this.settleMs);
      const list = this.timers.get(`${u}:${f.oid}`) ?? [];
      list.push(h);
      this.timers.set(`${u}:${f.oid}`, list);
    }
  }

  /** Test helper. */
  reset(): void {
    for (const list of this.timers.values()) for (const h of list) this.clearTimer(h);
    this.timers.clear();
    this.known.clear();
    this.seenTids.clear();
    this.terminal.clear();
    this.pending = [];
    this.away.clear();
  }

  // ---- internals ------------------------------------------------------------------------------

  private describe(k: Known, o: BasicOrder): string {
    if (k.label) return k.label;
    const orig = num(o.origSz);
    const action = t(o.side === 'B' ? 'ticket.buy' : 'ticket.sell');
    const label = this.fmt.label(o.coin);
    const px = this.fmt.px(o.limitPx, o.coin);
    return orig !== undefined
      ? t('orders.toast.describe', { action, sz: fmtSz(orig), label, px })
      : t('orders.toast.describe_no_sz', { action, label, px });
  }

  private prunePending(): void {
    const now = this.now();
    this.pending = this.pending.filter((p) => p.until >= now);
  }

  /** Remove and report the pending submit this order belongs to, if any. */
  private takePending(user: string, o: BasicOrder): boolean {
    this.prunePending();
    const px = Number(o.limitPx);
    const sz = Number(o.origSz ?? o.sz);
    const i = this.pending.findIndex(
      (p) => p.user === user && p.coin === o.coin && p.side === o.side && p.px === px && p.sz === sz,
    );
    if (i < 0) return false;
    this.pending.splice(i, 1);
    return true;
  }

  private entry(user: string, oid: number): Known {
    const key = `${norm(user)}:${oid}`;
    let k = this.known.get(key);
    if (!k) {
      k = { filled: 0 };
      this.known.set(key, k);
      if (this.known.size > 2_000) this.known.delete(this.known.keys().next().value as string);
    }
    return k;
  }

  private cancelTimers(user: string, oid: number): void {
    const key = `${norm(user)}:${oid}`;
    this.terminal.add(key);
    if (this.terminal.size > 5_000) this.terminal.delete(this.terminal.values().next().value as string);
    for (const h of this.timers.get(key) ?? []) this.clearTimer(h);
    this.timers.delete(key);
  }

  private dropTimer(user: string, oid: number, h: unknown): void {
    const key = `${user}:${oid}`;
    const list = (this.timers.get(key) ?? []).filter((x) => x !== h);
    if (list.length) this.timers.set(key, list);
    else this.timers.delete(key);
  }
}

function norm(user: string): string {
  return user.toLowerCase();
}

/** The app-wide tracker, feeding the app-wide toast queue. */
export const orderLifecycle = new OrderLifecycle({ sink: (t) => toasts.push(t) });
